"use client";

import { memo } from "react";
import { useScenePause } from "./useScenePause";

/**
 * Animated SVG illustration for the /map card on the landing page.
 * Hex mosaic over a light basemap. A pointer cursor takes a three-stop
 * tour across cheap → mid → luxury hexes; each landing fires a RIPPLE
 * PING (an expanding, fading ring - the mockup's sRipple pattern), the
 * hex pulses, and a tooltip springs in with that hex's AZN/m² price,
 * mirroring the real /map hover interaction. 7s loop with a quiet beat
 * at the seam; cursor travel uses the .22,1,.36,1 signature ease so each
 * move decelerates like a real hand.
 *
 * Pure SVG + CSS keyframes - no Leaflet on the landing page.
 * Transform/opacity only (no filter) so frames stay on the compositor.
 * memo()-wrapped to avoid re-rendering during resize.
 */
function MapAnimation() {
  const sceneRef = useScenePause<SVGSVGElement>();
  const r = 14;
  const W = 200;
  const H = 160;
  const cols: { x: number; ys: number[] }[] = [
    { x: 50,  ys: [50, 74, 98, 122] },
    { x: 71,  ys: [38, 62, 86, 110, 134] },
    { x: 92,  ys: [50, 74, 98, 122] },
    { x: 113, ys: [38, 62, 86, 110, 134] },
    { x: 134, ys: [50, 74, 98, 122] },
    { x: 155, ys: [38, 62, 86, 110, 134] },
  ];
  const palette = [
    "#1a9850", "#66bd63", "#a6d96a", "#d9ef8b", "#fee08b",
    "#fdae61", "#f46d43", "#d73027", "#a50026",
  ];
  const grid = [
    [0, 1, 2, 1],
    [1, 2, 3, 2, 1],
    [2, 3, 5, 4, 3],
    [3, 5, 8, 6, 5],
    [2, 4, 6, 5, 3],
    [1, 3, 4, 3, 2],
  ];

  const hexPath = (cx: number, cy: number) =>
    [0, 1, 2, 3, 4, 5]
      .map((i) => {
        const a = (Math.PI / 3) * i;
        const x = cx + r * Math.cos(a);
        const y = cy + r * Math.sin(a);
        return (i === 0 ? "M" : "L") + x.toFixed(1) + "," + y.toFixed(1);
      })
      .join(" ") + "Z";

  // Three hexes the cursor tours. Picked so the path reads as a random
  // scan rather than a straight line: top-left outskirt → far-right
  // mid-market → middle luxury pocket. Covers the full price gradient
  // and forces the cursor across the whole illustration.
  const stops: { cx: number; cy: number; price: string; hexClass: string; tipClass: string; ripClass: string }[] = [
    { cx: cols[0].x, cy: cols[0].ys[0], price: "1 800 AZN/m²", hexClass: "m-hex-1", tipClass: "m-tip-1", ripClass: "m-rip-1" },
    { cx: cols[5].x, cy: cols[5].ys[1], price: "2 900 AZN/m²", hexClass: "m-hex-2", tipClass: "m-tip-2", ripClass: "m-rip-2" },
    { cx: cols[3].x, cy: cols[3].ys[2], price: "7 200 AZN/m²", hexClass: "m-hex-3", tipClass: "m-tip-3", ripClass: "m-rip-3" },
  ];

  return (
    <svg ref={sceneRef} viewBox={`0 0 ${W} ${H}`} className="scene-svg w-full h-44" aria-hidden="true">
      {/* Background intentionally transparent - the card's white surface
          shows through, hex mosaic sits directly on it. */}

      {/* Hex mosaic. Each stop hex gets its own class so the keyframe
          can pulse it in sync with the cursor landing there. */}
      {cols.map((col, ci) =>
        col.ys.map((cy, ri) => {
          const v = grid[ci]?.[ri];
          if (v == null) return null;
          const stop = stops.find((s) => s.cx === col.x && s.cy === cy);
          return (
            <path
              key={`${ci}-${ri}`}
              d={hexPath(col.x, cy)}
              fill={palette[v]}
              fillOpacity={0.82}
              stroke="var(--color-paper-soft)"
              strokeWidth="1"
              className={stop?.hexClass}
              style={{ transformBox: "fill-box", transformOrigin: "center" }}
            />
          );
        }),
      )}

      {/* Ripple pings - an expanding, fading ring fires as the cursor
          lands on each stop (the hover "click" feedback). */}
      {stops.map((s) => (
        <circle
          key={s.ripClass}
          className={s.ripClass}
          cx={s.cx}
          cy={s.cy}
          r="13"
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="1.5"
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
        />
      ))}

      {/* Per-stop tooltips - each springs in while the cursor is parked
          on its hex. Ink-on-paper so they sit in the design language. */}
      {stops.map((s) => {
        // Wide enough for "7 200 AZN/m²" in Manrope (about 61 units); at
        // 54 the first digit and the ² fell outside the ink box.
        const tipW = 68;
        const tipH = 18;
        const tipX = s.cx - tipW / 2;
        const tipY = s.cy - tipH - 16;
        return (
          <g key={s.tipClass} className={s.tipClass}>
            <rect x={tipX} y={tipY} width={tipW} height={tipH} rx="2" fill="var(--color-ink)" />
            <path
              d={`M ${s.cx - 4} ${tipY + tipH} L ${s.cx} ${tipY + tipH + 5} L ${s.cx + 4} ${tipY + tipH} Z`}
              fill="var(--color-ink)"
            />
            <text x={s.cx} y={tipY + 12} textAnchor="middle" fill="var(--color-paper)" fontSize="9.5" fontWeight="500">
              {s.price}
            </text>
          </g>
        );
      })}

      {/* Pointer cursor - animated through the three stops. */}
      <g className="m-cursor">
        <path
          d="M 0 0 L 0 14 L 3.5 10.5 L 6.5 17 L 9 16 L 6 9.5 L 11 9.5 Z"
          fill="var(--color-ink)"
          stroke="var(--color-paper)"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </g>

      <style>{`
        /* ----------------------------------------------------
           7s loop, three-stop hover tour with ripple pings.
             0-8%    cursor fades in, heads to stop 1
             8-24%   parked on stop 1: ripple + hex pulse + tooltip
             24-32%  travels to stop 2
             32-48%  parked on stop 2
             48-56%  travels to stop 3 (luxury)
             56-84%  parked on stop 3
             90-100% everything fades, quiet beat, loop
           ---------------------------------------------------- */
        @keyframes mCursor {
          0%        { transform: translate(20px, 18px); opacity: 0; }
          4%        { opacity: 1; }
          8%, 24%   { transform: translate(${stops[0].cx - 2}px, ${stops[0].cy - 4}px); opacity: 1; }
          32%, 48%  { transform: translate(${stops[1].cx - 2}px, ${stops[1].cy - 4}px); opacity: 1; }
          56%, 84%  { transform: translate(${stops[2].cx - 2}px, ${stops[2].cy - 4}px); opacity: 1; }
          92%, 100% { transform: translate(${stops[2].cx - 2}px, ${stops[2].cy - 4}px); opacity: 0; }
        }
        @keyframes mTip1 {
          0%, 9%    { opacity: 0; transform: translateY(5px) scale(0.9); }
          13%, 22%  { opacity: 1; transform: translateY(0) scale(1); }
          26%, 100% { opacity: 0; transform: translateY(5px) scale(0.9); }
        }
        @keyframes mTip2 {
          0%, 33%   { opacity: 0; transform: translateY(5px) scale(0.9); }
          37%, 46%  { opacity: 1; transform: translateY(0) scale(1); }
          50%, 100% { opacity: 0; transform: translateY(5px) scale(0.9); }
        }
        @keyframes mTip3 {
          0%, 57%   { opacity: 0; transform: translateY(5px) scale(0.9); }
          61%, 82%  { opacity: 1; transform: translateY(0) scale(1); }
          88%, 100% { opacity: 0; transform: translateY(5px) scale(0.9); }
        }
        /* Ripples: fire once per landing - expand + fade, then stay hidden
           until that stop comes around again. */
        @keyframes mRip1 {
          0%, 8%    { opacity: 0; transform: scale(0.5); }
          10%       { opacity: 0.55; transform: scale(0.7); }
          22%, 100% { opacity: 0; transform: scale(1.7); }
        }
        @keyframes mRip2 {
          0%, 32%   { opacity: 0; transform: scale(0.5); }
          34%       { opacity: 0.55; transform: scale(0.7); }
          46%, 100% { opacity: 0; transform: scale(1.7); }
        }
        @keyframes mRip3 {
          0%, 56%   { opacity: 0; transform: scale(0.5); }
          58%       { opacity: 0.55; transform: scale(0.7); }
          70%, 100% { opacity: 0; transform: scale(1.7); }
        }
        /* Pulse via transform only - filter animations are NOT GPU
           composited and would force a full repaint of the hex grid
           every frame. Transform-only keyframes stay on the compositor. */
        @keyframes mHex1 {
          0%, 8%    { transform: scale(1); }
          14%, 20%  { transform: scale(1.10); }
          26%, 100% { transform: scale(1); }
        }
        @keyframes mHex2 {
          0%, 32%   { transform: scale(1); }
          38%, 46%  { transform: scale(1.10); }
          52%, 100% { transform: scale(1); }
        }
        @keyframes mHex3 {
          0%, 56%   { transform: scale(1); }
          62%, 80%  { transform: scale(1.14); }
          88%, 100% { transform: scale(1); }
        }
        .m-cursor { animation: mCursor 7s cubic-bezier(.22,1,.36,1) infinite; transform-box: fill-box; }
        .m-tip-1  { animation: mTip1 7s cubic-bezier(0.34,1.56,0.64,1) infinite; transform-box: fill-box; transform-origin: center bottom; }
        .m-tip-2  { animation: mTip2 7s cubic-bezier(0.34,1.56,0.64,1) infinite; transform-box: fill-box; transform-origin: center bottom; }
        .m-tip-3  { animation: mTip3 7s cubic-bezier(0.34,1.56,0.64,1) infinite; transform-box: fill-box; transform-origin: center bottom; }
        .m-rip-1  { animation: mRip1 7s ease-out infinite; opacity: 0; }
        .m-rip-2  { animation: mRip2 7s ease-out infinite; opacity: 0; }
        .m-rip-3  { animation: mRip3 7s ease-out infinite; opacity: 0; }
        .m-hex-1  { animation: mHex1 7s ease-in-out infinite; }
        .m-hex-2  { animation: mHex2 7s ease-in-out infinite; }
        .m-hex-3  { animation: mHex3 7s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .m-cursor, .m-tip-1, .m-tip-2, .m-tip-3, .m-rip-1, .m-rip-2, .m-rip-3,
          .m-hex-1, .m-hex-2, .m-hex-3 { animation: none; }
          .m-cursor { transform: translate(${stops[2].cx - 2}px, ${stops[2].cy - 4}px); opacity: 1; }
          .m-tip-3  { opacity: 1; transform: none; }
          .m-tip-1, .m-tip-2, .m-rip-1, .m-rip-2, .m-rip-3 { opacity: 0; }
        }
      `}</style>
    </svg>
  );
}

export default memo(MapAnimation);
